import { Component, inject, input, LOCALE_ID, OnDestroy, OnInit, output, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { UserListFilter } from '../../../../../services/auth-service.model';
import { AuthService } from '../../../../../services/auth-service';
import { LookupVTO } from '../../../../../pages/admin-dashboard/admin-companies/admin-companies.model';

const DEFAULT_FILTERS: UserListFilter = {
  orderBy: 'CREATION_DATE',
  orderDir: 'DESC',
};

@Component({
  selector: 'app-filter',
  imports: [FormsModule, ReactiveFormsModule, CommonModule],
  templateUrl: './filter.html',
  styleUrl: './filter.scss',
  standalone: true,
})
export class Filter implements OnInit, OnDestroy {
  private locale = inject(LOCALE_ID);
  private authService = inject(AuthService);

  filters = input.required<UserListFilter>();
  rolesList = input<LookupVTO[]>([]);

  applyFilters = output<UserListFilter>();
  resetFilters = output();
  toggleSortOrder = output();

  isRoleMenuOpen = signal<boolean>(false);
  isStatusMenuOpen = signal<boolean>(false);

  selectedRoleId = signal<number | null>(null);
  selectedStatusId = signal<number | null | undefined>(undefined);
  localFilters = signal<UserListFilter>({ ...DEFAULT_FILTERS });

  fullNameResults = signal<{ id: number; name: string }[]>([]);
  showFullNameDropdown = signal<boolean>(false);
  private fullNameSearchTimeout: any;

  createdFromError = signal<string>('');
  createdToError = signal<string>('');
  updatedFromError = signal<string>('');
  updatedToError = signal<string>('');

  readonly today = new Date().toISOString().split('T')[0];

  ngOnInit(): void {
    this.localFilters.set({
      ...DEFAULT_FILTERS,
      ...this.filters(),
    });
    this.selectedRoleId.set(this.filters().roleIds?.[0] ?? null);
    this.selectedStatusId.set(this.filters().statusId);
  }

  ngOnDestroy(): void {
    clearTimeout(this.fullNameSearchTimeout);
  }

  isArabic(): boolean { return this.locale.startsWith('ar'); }

  updateFilter(key: keyof UserListFilter, value: any): void {
    this.localFilters.set({ ...this.localFilters(), [key]: value });
    this.validateDates();
  }

  onFullNameChange(value: string): void {
    this.updateFilter('fullName', value);
    clearTimeout(this.fullNameSearchTimeout);
    if (!value || value.length < 2) {
      this.fullNameResults.set([]);
      this.showFullNameDropdown.set(false);
      return;
    }
    this.showFullNameDropdown.set(true);
    this.fullNameSearchTimeout = setTimeout(() => this.searchFullNames(value), 400);
  }

  onFullNameFocus(): void {
    const fullName = this.localFilters().fullName;
    if (fullName && fullName.length >= 2 && !this.fullNameResults().length) {
      this.searchFullNames(fullName);
    }
    if (fullName && fullName.length >= 2) this.showFullNameDropdown.set(true);
  }

  selectFullName(item: { id: number; name: string }): void {
    this.updateFilter('fullName', item.name);
    this.showFullNameDropdown.set(false);
  }

  private searchFullNames(term: string): void {
    this.authService.getAllUsersByFilters({ fullName: term, pageSize: 10 }).subscribe({
      next: (res) => {
        const results = (res.list || []).map((u) => ({
          id: u.id,
          name: `${u.firstName} ${u.lastName}`.trim(),
        }));
        this.fullNameResults.set(results);
      },
      error: () => this.fullNameResults.set([]),
    });
  }

  onToggleRoleMenu(event: Event): void {
    event.stopPropagation();
    this.isRoleMenuOpen.set(!this.isRoleMenuOpen());
    this.isStatusMenuOpen.set(false);
  }

  selectRole(roleId: number | null): void {
    this.selectedRoleId.set(roleId);
    this.isRoleMenuOpen.set(false);
  }

  onToggleStatusMenu(event: Event): void {
    event.stopPropagation();
    this.isStatusMenuOpen.set(!this.isStatusMenuOpen());
    this.isRoleMenuOpen.set(false);
  }

  selectStatus(statusId: number | null): void {
    this.selectedStatusId.set(statusId);
    this.isStatusMenuOpen.set(false);
  }

  getStatusLabel(statusId: number | null | undefined): string {
    const isAr = this.isArabic();
    switch (statusId) {
      case 2: return isAr ? 'نشط' : 'Active';
      case 1: return isAr ? 'غير نشط' : 'Inactive';
      case 3: return isAr ? 'محذوف' : 'Deleted';
      default: return isAr ? 'جميع الحالات' : 'All Statuses';
    }
  }

  getRoleLabel(): string {
    const id = this.selectedRoleId();
    if (id === null) return this.isArabic() ? 'كل الوظائف' : 'All Roles';
    const role = this.rolesList().find((r) => r.id === id);
    if (!role) return this.isArabic() ? 'كل الوظائف' : 'All Roles';
    return this.isArabic() ? (role.titleAr ?? '') : (role.titleEn ?? '');
  }

  getStatusClass(statusId: number | null | undefined): string {
    switch (statusId) {
      case 2: return 'bg-green-50 text-green-700 border-green-200';
      case 1: return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 3: return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-gray-50 text-gray-500 border-gray-200';
    }
  }

  validateDates(): void {
    const f = this.localFilters();
    const isAr = this.isArabic();

    this.createdFromError.set('');
    this.createdToError.set('');
    this.updatedFromError.set('');
    this.updatedToError.set('');

    if (f.createdFrom && f.createdTo && f.createdFrom > f.createdTo) {
      this.createdFromError.set(isAr ? 'تاريخ البداية لا يمكن أن يكون بعد تاريخ النهاية' : 'From date cannot be after To date');
      this.createdToError.set(isAr ? 'تاريخ النهاية لا يمكن أن يكون قبل تاريخ البداية' : 'To date cannot be before From date');
    }

    if (f.lastModifiedOnFrom && f.lastModifiedOnTo && f.lastModifiedOnFrom > f.lastModifiedOnTo) {
      this.updatedFromError.set(isAr ? 'تاريخ البداية لا يمكن أن يكون بعد تاريخ النهاية' : 'From date cannot be after To date');
      this.updatedToError.set(isAr ? 'تاريخ النهاية لا يمكن أن يكون قبل تاريخ البداية' : 'To date cannot be before From date');
    }
  }

  hasDateErrors(): boolean {
    return !!this.createdFromError() || !!this.createdToError() || !!this.updatedFromError() || !!this.updatedToError();
  }

  onApplyFilters(): void {
    if (this.hasDateErrors()) return;
    this.applyFilters.emit({
      ...this.localFilters(),
      roleIds: this.selectedRoleId() !== null ? [this.selectedRoleId()!] : [],
      statusId: this.selectedStatusId(),
    });
  }

  onResetFilters(): void {
    this.localFilters.set({ ...DEFAULT_FILTERS });
    this.selectedRoleId.set(null);
    this.selectedStatusId.set(undefined);
    this.fullNameResults.set([]);
    this.showFullNameDropdown.set(false);
    this.createdFromError.set('');
    this.createdToError.set('');
    this.updatedFromError.set('');
    this.updatedToError.set('');
    this.resetFilters.emit();
  }

  onToggleSortOrder(): void {
    this.toggleSortOrder.emit();
  }

  closeAllDropdowns(): void {
    this.isRoleMenuOpen.set(false);
    this.isStatusMenuOpen.set(false);
    this.showFullNameDropdown.set(false);
  }
}